import { Box, Button, CircularProgress, Grid2 as Grid, IconButton, Typography } from '@mui/material'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import Paper from 'components/@extended/Paper'
import { useSnackbar } from 'notistack'
import React, { useRef, useState } from 'react'
import { deleteWithToken, postWithToken } from 'hooks/http'

interface CompanyLogoCardProps {
  organizationId: string
  token: string
  isRootOrganization: boolean
  initialLogoUrl?: string | null
  onLogoChange?: (logoUrl: string | null) => void
}

const CompanyLogoCard: React.FC<CompanyLogoCardProps> = ({
  organizationId,
  token,
  isRootOrganization,
  initialLogoUrl,
  onLogoChange,
}) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl || null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { enqueueSnackbar } = useSnackbar()

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      enqueueSnackbar('Invalid file type. Please upload a PNG, JPEG, GIF, or WebP image.', { variant: 'error' })
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('File size exceeds 5MB limit.', { variant: 'error' })
      return
    }

    // Convert to base64 and upload
    const reader = new FileReader()
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1] // Remove data URL prefix
      uploadLogo(base64Data, file.type)
    }
    reader.onerror = () => {
      enqueueSnackbar('Failed to read file.', { variant: 'error' })
    }
    reader.readAsDataURL(file)
  }

  const uploadLogo = (imageData: string, contentType: string) => {
    setIsUploading(true)
    postWithToken(`organizations/${organizationId}/logo`, token, {
      imageData,
      contentType,
    })
      .then((response) => {
        setLogoUrl(response.logoUrl)
        onLogoChange?.(response.logoUrl)
        enqueueSnackbar('Logo uploaded successfully', { variant: 'success' })
      })
      .catch((error) => {
        console.error('Failed to upload logo:', error)
        enqueueSnackbar('Failed to upload logo', { variant: 'error' })
      })
      .finally(() => {
        setIsUploading(false)
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      })
  }

  const handleDelete = () => {
    setIsUploading(true)
    deleteWithToken(`organizations/${organizationId}/logo`, token)
      .then(() => {
        setLogoUrl(null)
        onLogoChange?.(null)
        enqueueSnackbar('Logo deleted successfully', { variant: 'success' })
      })
      .catch((error) => {
        console.error('Failed to delete logo:', error)
        enqueueSnackbar('Failed to delete logo', { variant: 'error' })
      })
      .finally(() => {
        setIsUploading(false)
      })
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  // Don't render if not a root organization
  if (!isRootOrganization) {
    return null
  }

  return (
    <Paper elevation={0} sx={{ height: '100%' }}>
      <Typography variant="h5" gutterBottom mb={2}>
        Company Logo
      </Typography>
      <Grid container spacing={3} p={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              width: 200,
              height: 100,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.default',
              overflow: 'hidden',
            }}
          >
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Company Logo"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No logo uploaded
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={isUploading ? <CircularProgress size={16} /> : <UploadOutlined />}
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              {logoUrl ? 'Change Logo' : 'Upload Logo'}
            </Button>
            {logoUrl && (
              <IconButton onClick={handleDelete} disabled={isUploading} color="error" size="small" title="Delete Logo">
                <DeleteOutlined />
              </IconButton>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Supported formats: PNG, JPEG, GIF, WebP. Max size: 5MB.
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default CompanyLogoCard
